import { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  Box,
  Typography,
  Backdrop, CircularProgress 
} from "@mui/material";
import { Scatter, Line, XAxis, YAxis, ComposedChart, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme } from "@mui/material/styles";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { cloneDeep, isEmpty, isEqual, values, isNull } from "lodash";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import "@plannel/scss/components/DpWorkbench.scss";

import Snackbar from "@plannel/components/Snackbar";
import SplitterContainer from "@plannel/components/SplitterContainer";
import { ResetButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DataState from "@plannel/components/aggrid/DataState"
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter";
import { DPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";

import appConfigService from "@plannel/services/system/app-config-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import custHrchyService from "@plannel/services/data/customer-hrchy-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import bfFeatureAnalysisService from "@plannel/services/dp/bf-feature-analysis-service";

import WorkbenchUtil from "@plannel/pages/demand-plan/dp-workbench/WorkbenchUtil";
import codeUtil from "@plannel/pages/utils/code-util";
import colorUtil from "@plannel/pages/utils/color-util";
import dateUtil from '@plannel/utils/date-util';
import reduxUtil from "@plannel/utils/redux-util";
import numberUtil from "@plannel/utils/number-util";
import ElasticSearchUtil from "@plannel/pages/utils/elastic-search-util";
import debug from "@plannel/utils/debug-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import { AgTooltipBox, AgTooltipRow } from "@plannel/components/rechart/AgStyleTooltip";
import { FS } from "@plannel/utils/font-util";
 

const CURRENT_DATE = new ZDate(new ZDate().getFullYear(), new ZDate().getMonth() - 1, new ZDate().getDate(), 0, 0, 0);
const DEFAULT_START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 3, CURRENT_DATE.getMonth() - 1, CURRENT_DATE.getDate(), 0, 0, 0);
const MIN_START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1, 0, 0, 0);
const MAX_START_DATE = new ZDate(CURRENT_DATE.getFullYear() + 1, CURRENT_DATE.getMonth() + 1, 0, 23, 59, 59);
const FEATURE_ANALYSIS_FEATURE_NAME = "FEATURE_ANALYSIS_FEATURE_NAME";

const FeatureAnalysis = ({ t, title, viewName }) => {
  const theme = useTheme();
  const reduxViewState = cloneDeep(reduxUtil.getViewState(viewName));
  const reduxDispatch = useDispatch();

  const { colorSet, snackBarMessageColor } = colorUtil;
  const { buckets, module, dayOfWeek, dpConfig, isWeekBucket } = codeUtil;

  const gridRef = useRef();
  const [initSelect, setInitSelect] = useState(false);

  const [gridData, setGridData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [featureData, setFeatureData] = useState(reduxViewState?.featureData ?? {});
  const [yAxisAvg, setYAxisAvg] = useState(0);
  const [lineAxisAvg, setlineAxisAvg] = useState(0);

  const [salesData, setSalesData] = useState([]);
  const [filterStartDate, setFilterStartDate] = useState(reduxViewState?.startDate ? new ZDate(reduxViewState.startDate) : DEFAULT_START_DATE);
  const [filterEndDate, setFilterEndDate] = useState(reduxViewState?.endDate ? new ZDate(reduxViewState.endDate) : CURRENT_DATE);

  const [open, setOpen] = useState(false);
  const [reload, setReload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [planConfigFlg, setPlanConfigFlg] = useState(reduxViewState?.planConfigFlg ?? false);

  const [filterValue, setFilterValue] = useState(reduxViewState?.filterValue ?? {});
  const [treeload, setTreeload] = useState(false);
  const [defaultValue, setDefaultValue] = useState(null);
  const [defaultCustomerHrchy, setDefaultCustomerHrchy] = useState(null);

  const [scatterXConfig, setScatterXConfig] = useState()
  const [lineConfig, setLineConfig] = useState()
  const [yAxisDomain, setYAxisDomain]= useState([0, 1]);

  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [bucket, setBucket] = useState(reduxViewState?.bucket ?? buckets.MONTH);
  const [startDay, setStartDay] = useState(reduxViewState?.startDay ?? dayOfWeek.MONDAY.cd);
  const { DP } = module;

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const importanceConvertName = {
    CUSTOMER_CD: "customerCd",
    CUSTOMER_NM: "customerName",
    ITEM_CD: "itemCd",
    ITEM_NM: "itemName",
    IMPORTANCE_GB: "importanceGb",
    IMPORTANCE_RF: "importanceRf",
    P_VALUE: "pvalue",
    R_VALUE: "rvalue",
    FACTOR_CD: "feature",
    REG_COEF: "regCoef"
  }

  const chartSeries = [
    {
      field: "importanceRf",
      text: t("importanceRf"),
      type: codeUtil.toggleType.BAR,
      typeOptions: { fill: colorSet(0) },
      orientation: "left",
      groupName: t("featureImportance")
    },
    {
      field: "importanceGb",
      text: t("importanceGb"),
      type: codeUtil.toggleType.BAR,
      typeOptions: { fill: colorSet(1) },
      orientation: "left",
      groupName: t("featureImportance")
    },
  ];

  const getWeekStartDate = (curDate) => {
    const weekDay = values(dayOfWeek).filter(week => isEqual(week.cd, startDay))[0];
    const day = curDate.getDay();

    const convertFlg = WorkbenchUtil.ableDayOfWeek(curDate, bucket, startDay);
    const computeDays = Number(weekDay.id);

    if (convertFlg) {
      const convertDate = Number(curDate.getDate() - day + computeDays);
      const monthAllDays = new ZDate(curDate.getFullYear(), curDate.getMonth() + 1, 0).getDate();
      if (convertDate > monthAllDays) {
        return new ZDate(curDate.getFullYear(), curDate.getMonth() + 1, convertDate - monthAllDays);
      }
      return new ZDate(curDate.setDate(convertDate));
    }
    return curDate;
  };

  const numberFormatter = (number, average, digits = 1) => {
    return numberUtil.formatNumberByAvg(number, average, digits);
  }

  const retrieveDpSetting = () => {
    const param = {
      moduleCd: DP,
      activeFlg: true,
    };

    planConfigService.getAll(param)
    .then(response => {
      setPlanConfigFlg(true);
      if (response.data) {
        for (const result of response.data.results) {
          if (result.configCd === dpConfig.START_DAY) {
            setStartDay(result.strVal);
          } else if (result.configCd === dpConfig.BUCKET_UNIT) {
            setBucket(result.strVal)
          } else if (result.configCd === dpConfig.BF_PARAMETER) {
            if (result.objVal?.trainingPeriod?.period) {
              const periodRange = result.objVal?.trainingPeriod?.period;
              const date = new ZDate(new ZDate().getFullYear(), new ZDate().getMonth() - 1, 1, 0, 0, 0);
              date.setMonth(date.getMonth() - (periodRange-1));
              setFilterStartDate(date)
            } else if (result.objVal?.trainingPeriod?.date) {
              setFilterStartDate(new ZDate(result.objVal?.trainingPeriod?.date))
            } else {
              setFilterStartDate(DEFAULT_START_DATE)
            }
          }
        }
      }
    })
    .catch(e => {
      debug(e);
    });
  }

  const getRequestParams = () => {
    return {
      startDate: isWeekBucket(bucket) ? filterStartDate : dateUtil.startOfMonthDate(filterStartDate), 
      endDate: isWeekBucket(bucket) ? filterEndDate : dateUtil.endOfMonthDate(filterEndDate),
      customerHrchyId: customerSearch.current ? null : filterValue.customerHrchy?.id,
      itemHrchyId: itemSearch.current ? null : filterValue.category?.id,
      customerCd: customerSearch.current,
      itemCd:  itemSearch.current
    };
  }

  /*
     {date: [{feature:"feature_01", featureName:"Temperature", activeFlg: true}, {feature:"feature_02", featureName:"Humidity", activeFlg: true}], 
     sales: [{feature:"feature_01", featureName:"Discount Rate", activeFlg: true}]}
     기존 obj_val 데이터를 아래와 같이 변경
     {date: {feature_01:"Temperature", feature_02:"Humidity"}, sales: {feature_01:"Discount Rate"}}
  */
  const makeFeatureNameFormat = (features) => {
    const typeFormat = {}
    for (const type of Object.keys(features)) {
      const featureFormat = {}; 
      for (const feature of features[type]) {
        if (feature.activeFlg) {
          featureFormat[feature.feature] = feature.featureName;
        }
      }
      typeFormat[type] = featureFormat;
    }
    setFeatureData(typeFormat);
  }

  const retrieveFeatureSetting = () => {
    appConfigService.getAppConfigs({configCds: [FEATURE_ANALYSIS_FEATURE_NAME]})
    .then(response => {
      const { data } = response;
      if (data) {
        makeFeatureNameFormat(data[0].objVal)
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoFeatureConfig") });
      }
    })
    .catch(e => {
      debug(e);
      setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoFeatureConfig") });
    });
  }

  const retrieveActualSales = (scatterData) => {
    //{"Discount rate": 23, "salesAmt": 566, "Holiday weight": 0.2561, "CCSI": 0.244}
    const ObjectKey = ["y", "sales", "date"]
    const data = [];
    for (const row of scatterData) {
      const insertRow = {};
      for (const key of ObjectKey) {
        if (key === "y") {
          insertRow.salesQty = row[key]
        } else {
          for (const name of Object.keys(row[key])) {
            insertRow[featureData[key][name]] = row[key][name];
          }
        }
      }
      data.push(insertRow);
    }
    return data;
  }

  const retrieveTopItemHrchies = () => {
    itemHrchyService.getTopHrchy({ hrchyNum: codeUtil.hrchyNum.HRCHY, isPermnCheck: true })
    .then(response => {
      const hrchy = response.data;
      if (hrchy !== null) {
        setDefaultValue(hrchy);
      }
    })
    .catch(e => {
      debug(e);
    });
  }

  const retrieveTopCustHrchies = () => {
    custHrchyService.getTopHrchy({ hrchyNum: codeUtil.hrchyNum.HRCHY, isPermnCheck: true })
    .then(response => {
      const hrchy = response.data;
      if (hrchy !== null) {
        setDefaultCustomerHrchy(hrchy);
      }
    })
    .catch(e => {
      debug(e);
    });
  }

  const getAvgAxisValue = (minValue, maxValue) => {
    return (minValue + maxValue) / 2;
  }

  const makeRegressionData = (formulas, scatterData) => {
    const data = retrieveActualSales(scatterData);
    if (isEmpty(data)) {
      return;
    }
    let minValue = 0;
    let maxValue = 0;
    for (const row of data) {
      if (row.salesQty) {
        if (row.salesQty < minValue) {
          minValue = row.salesQty;
        } else if (row.salesQty > maxValue) {
          maxValue = row.salesQty;
        }
      }
      for (const key of Object.keys(row)) {
        const name = `reg_${key}`;
        if (formulas[key]){
          row[name] = Math.floor((formulas[key].REG_COEF * row[key]) + formulas[key].CONSTANT, 2)
        }
      }
    }
    setYAxisAvg(getAvgAxisValue(minValue, maxValue));
    setSalesData(data)
  }

  const setGridDatas = (importanceData, scatterData) => {
    if (isEmpty(importanceData)) {
      setOpen(false);
      setGridData([]);
      setChartData([]);
      setSalesData([]);
      setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
    }
    const data = [];
    const formulas = {};
    if (Object.keys(featureData).length > 0) {
      let maxYAxis = 0.001;
      for (const row of importanceData) {
        const insertRow = {};
        const type = row.TYPE; //"date"
        const featureCd = row.FACTOR_CD; //"feature_01"
        if (!featureData[type][featureCd]) {
          continue;
        }
        for (const key of Object.keys(importanceConvertName)) {
          if (key === "FACTOR_CD") {
            insertRow[importanceConvertName[key]] = featureData[type][featureCd];
            formulas[featureData[type][featureCd]] = {REG_COEF: row.REG_COEF, CONSTANT: row.CONSTANT};  
          } else {
            if (!isNaN(row[key])) {
              if ((key === "IMPORTANCE_GB" || key === "IMPORTANCE_RF") 
              && parseFloat(row[key]?.toFixed(3)) > maxYAxis) {
                maxYAxis = parseFloat(row[key]?.toFixed(3));
              }
              insertRow[importanceConvertName[key]] = parseFloat(row[key]?.toFixed(3));
            } else {
              insertRow[importanceConvertName[key]] = row[key];
            }
          }
        }
        setYAxisDomain([0, maxYAxis]);
        data.push(insertRow);
      }
    }
    setGridData(data);
    makeRegressionData(formulas, scatterData);
    setOpen(false);
  }

  const retrieveFeatrueAnalysis = () => {
    setOpen(true);
    setIsLoading(false);
    setInitSelect(true);
    const params = getRequestParams();
    if (params.customerCd === "" && !params.customerHrchyId) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoCustomer") });
      setOpen(false);
      return;
    }
    if (params.itemCd === "" && !params.itemHrchyId) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoItem") });
      setOpen(false);
      return;
    }
    bfFeatureAnalysisService.getAll(params)
    .then(response => {
      if (response.data?.message) {
        const { message } = response.data;
        setOpen(false);
        setSalesData([]);
        setGridData([]);
        setChartData([]);
        setSnackInfo({ open: true, severity: message.type, content: t(message.message)});
        return;
      }
      if (response.data.results) {
        const parseData = JSON.parse(response.data.results);
        setGridDatas(parseData.importance_result, parseData.relation_result);
      } else {
        setOpen(false);
        setSalesData([]);
        setGridData([]);
        setChartData([]);
        setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
      }
    })
    .catch(e => {
      debug(e);
      setOpen(false);
      setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
    })
    .then(() => {
      const reduxObjState = reduxUtil.createObjectState(viewName, {filterValue, itemSearch: itemSearch.current, featureData,
        customerSearch: customerSearch.current, startDate: params.startDate, endDate: params.endDate, bucket, startDay, planConfigFlg});
      reduxDispatch(reduxUtil.updateViewState({ ...reduxObjState }));
      setTreeload(false);
    });
  }

  const decimalFormatter = (e) => {
    if (!isNull(e.value)) {
      return parseFloat(e.value?.toFixed(3));
    }
    return e.value
  }

  const pValueFormatter = (e) => {
    if (!isNull(e.value) && Number.isFinite(e.value)) {
      return e.value.toFixed(3);
    }
    return e.value
  }

  const columnDefs = [
    {
      headerName: "customerCd",
      colId: "customerId",
      showRowGroup: "customerCd",
      cellRenderer: "agGroupCellRenderer",
      chartDataType: "category",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      cellRendererParams: {
        suppressCount: true,
      },
      width: 200,
      minWidth: 100
    },
    {
      headerName: "customerCd",
      field: "customerCd",
      editable: false,
      pinned: "left", lockPinned: true,
      rowGroupIndex: 3,
      hide: true,
      cellClass: "stringType",
      rowGroup: true,
    },
    {
      headerName: "customerName",
      colId: "customerNameId",
      showRowGroup: "customerName",
      cellRenderer: "agGroupCellRenderer",
      chartDataType: "category",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      cellRendererParams: {
        suppressCount: true,
      },
      width: 200,
      minWidth: 100
      
    },
    {
      headerName: "customerName",
      field: "customerName",
      editable: false,
      pinned: "left", lockPinned: true,
      rowGroupIndex: 4,
      hide: true,
      cellClass: "stringType",
      rowGroup: true,
    },
    {
      headerName: "itemCd",
      colId: "itemId",
      showRowGroup: "itemCd",
      chartDataType: "category",
      pinned: "left", lockPinned: true,
      cellRenderer: "agGroupCellRenderer",
      cellClass: "stringType",
      cellRendererParams: {
        suppressCount: true,
      },
      width: 200,
      minWidth: 100
    },
    {
      headerName: "itemCd",
      field: "itemCd",
      editable: false,
      pinned: "left", lockPinned: true,
      rowGroupIndex: 0,
      hide: true,
      cellClass: "stringType",
      rowGroup: true,
    },
    {
      headerName: "itemName",
      colId: "itemNameId",
      showRowGroup: "itemName",
      chartDataType: "category",
      pinned: "left", lockPinned: true,
      cellRenderer: "agGroupCellRenderer",
      cellClass: "stringType",
      cellRendererParams: {
        suppressCount: true,
      },
      width: 200,
      minWidth: 100
    },
    {
      headerName: "itemName",
      field: "itemName",
      editable: false,
      pinned: "left", lockPinned: true,
      rowGroupIndex: 0,
      hide: true,
      cellClass: "stringType",
      rowGroup: true,
    },
    { headerName: "feature", field: "feature", width: 200, minWidth: 100, pinned: "left", lockPinned: true, cellClass: "stringType" },
    { headerName: "importanceRf", field: "importanceRf", width: 210, minWidth: 100, type: ["rightAligned", "nonEditableColumn"], valueFormatter: decimalFormatter},
    { headerName: "importanceGb", field: "importanceGb", width: 210, minWidth: 100,type: ["rightAligned", "nonEditableColumn"], valueFormatter: decimalFormatter},
    { headerName: "P-Value", field: "pvalue", width: 185, minWidth: 100, type: ["rightAligned", "nonEditableColumn"], valueFormatter: pValueFormatter },
    { headerName: "rvalue", field: "rvalue", width: 185, minWidth: 100, type: ["rightAligned", "nonEditableColumn"], valueFormatter: decimalFormatter }
  ];
 
  const onCellClicked = e => {
    const chart = [];
    e.node.setSelected(true)
    for (const row of e.node?.parent?.allLeafChildren) {
      if (row?.data?.feature && row?.data?.importanceGb && row?.data?.importanceRf) {
        const data = {feature: row.data.feature, importanceGb: row.data.importanceGb, importanceRf: row.data.importanceRf }
        chart.push(data)
      }
    }
    setChartData(chart);
    const xAxisName = e.node.data.feature;
    salesData.sort((a, b) => {
      if (a[xAxisName] < b[xAxisName]) {
        return -1;
      }
      if (a[xAxisName] > b[xAxisName]) {
        return 1;
      }
      return 0;
    });

    const lineName = `reg_${xAxisName}`;
    if (salesData && !isEmpty(salesData)) {
      const minValue = salesData?.[0]?.[xAxisName] ?? 0;
      const maxValue = salesData?.[salesData.length - 1]?.[xAxisName] ?? 0;
      setlineAxisAvg(getAvgAxisValue(salesData[0]?.[xAxisName], salesData[salesData.length - 1]?.[xAxisName]))
      setScatterXConfig(<XAxis type="number" dataKey={xAxisName} name={xAxisName} tickFormatter={(tick) => numberFormatter(tick, getAvgAxisValue(minValue, maxValue))} fontSize={FS.BODY} label={{ value: xAxisName , offset: 1, position: "bottom", dy: 5, fontSize: FS.H2, fill: theme.palette.text.primary }}/>)
      setLineConfig(<Line dataKey={lineName} name={xAxisName} stroke={colorSet(1)} dot={false} strokeWidth={4}/> )
    }
  };

  const handleReset = () => {
    reduxDispatch(reduxUtil.updateViewState({}));
    itemSearch.current = "";
    customerSearch.current = "";
    setTreeload(true);
    setReload(true);
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  useEffect(() => {
    //다시 로드했을 때 redux value와 filtervalue가 같으면 검색
    if (isLoading && isEqual(reduxViewState?.filterValue?.customerHrchy, filterValue?.customerHrchy) &&
    isEqual(reduxViewState?.filterValue?.category, filterValue?.category)) {
      retrieveFeatrueAnalysis();
    } else {
      if (!isLoading && filterValue.customerHrchy && filterValue.category) { 
        retrieveFeatrueAnalysis();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  useEffect(() => {
    if (gridData.length > 0 && initSelect) {
      const selectedRows = [];
      let firstLeafNode = null;
      gridRef.current.api?.forEachLeafNode(node => { if (!firstLeafNode) firstLeafNode = node; });
      if (firstLeafNode) {
        firstLeafNode.setSelected(true);
        for (const leaf of firstLeafNode.parent?.allLeafChildren ?? []) {
          selectedRows.push(leaf.data);
        }
      }
      setChartData(selectedRows);
      setInitSelect(false)

      const xAxisName = selectedRows[0]?.feature;
      const lineName = `reg_${xAxisName}`;

      salesData.sort((a, b) => {
        if (a[xAxisName] < b[xAxisName]) {
          return -1;
        }
        if (a[xAxisName] > b[xAxisName]) {
          return 1;
        }
        return 0;
      });
      const minValue = salesData[0][xAxisName];
      const maxValue = salesData[salesData.length - 1][xAxisName];

      setlineAxisAvg(getAvgAxisValue(salesData[0][xAxisName], salesData[salesData.length - 1][xAxisName]))
      setScatterXConfig(<XAxis type="number" dataKey={xAxisName} name={xAxisName} tickFormatter={(tick) => numberFormatter(tick, getAvgAxisValue(minValue, maxValue))} fontSize={FS.BODY} label={{ value: xAxisName , offset: 1, position: "bottom", dy: 5, fontSize: FS.H2, fill: theme.palette.text.primary }}/>)
      setLineConfig(<Line dataKey={lineName} name={xAxisName} stroke={colorSet(1)} dot={false} strokeWidth={4}/> )
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridData])
  
  useEffect(() => {
    if (isLoading && Object.keys(featureData).length > 0) {
      retrieveFeatrueAnalysis();
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  },[featureData])

  useEffect(() => {
    if (isEmpty(reduxViewState?.featureData) && 
    defaultCustomerHrchy && defaultValue && Object.keys(defaultCustomerHrchy).length > 0 && Object.keys(defaultValue).length > 0) {
      retrieveFeatureSetting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCustomerHrchy, defaultValue])
  
  useEffect(() => {
    if (isLoading) {
      DataState.initialize(gridRef.current.api)
      setInitSelect(true);
      retrieveTopCustHrchies();
      retrieveTopItemHrchies();
      if (!reduxViewState?.planConfigFlg) {
        retrieveDpSetting();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  useEffect(() => {
    if (!isLoading) {
      setIsLoading(true);
    } 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reload) {
      retrieveFeatrueAnalysis();
      setReload(false)
    }
   // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload])

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
      const itemData = payload[0].payload;
      const salesQty = payload[0]?.name;
      const feature = payload[1]?.name;

      return (
        // 색은 아래 Line/Scatter 의 stroke/fill 과 같은 값 — 심볼 색으로 그대로 옮겼다
        <AgTooltipBox>
          <AgTooltipRow color={colorSet(1)} label={feature} value={numberFormatter(itemData[feature], lineAxisAvg)} />
          <AgTooltipRow color={colorSet(0)} label={salesQty} value={numberFormatter(itemData?.salesQty, yAxisAvg)} />
        </AgTooltipBox>
      );
    }

    return null;
  };

  return (
    <>
      <FilterContainer hasIcons={false}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t("periodStart")}
            views={isWeekBucket(bucket) ? ["year", "month", "day"] : ["year", "month"]}
            format={isWeekBucket(bucket) ? "yyyy-MM-dd" : "yyyy-MM"}
            minDate={MIN_START_DATE}
            maxDate={MAX_START_DATE}
            value={isWeekBucket(bucket) ? getWeekStartDate(filterStartDate) : filterStartDate}
            shouldDisableDate={(e) => WorkbenchUtil.ableDayOfWeek(e, bucket, startDay)}
            openTo={"month"}
            onAccept={newStartValue => {
              newStartValue ? setFilterStartDate(newStartValue) : setFilterStartDate(filterStartDate);
              setReload(true);
            }}
            slotProps={{ textField: { size: "small", sx: { mr: 1, width: 150 } } }}
          />
          <DatePicker
            label={t("periodEnd")}
            views={isWeekBucket(bucket) ? ["year", "month", "day"] : ["year", "month"]}
            format={isWeekBucket(bucket) ? "yyyy-MM-dd" : "yyyy-MM"}
            minDate={filterStartDate}
            maxDate={bucket === buckets.MONTH ? new ZDate(dateUtil.endOfMonthDate(CURRENT_DATE).getFullYear(), dateUtil.endOfMonthDate(CURRENT_DATE).getMonth() + 1, 0, 23, 59, 59) : CURRENT_DATE}
            value={filterEndDate}
            openTo={"month"}
            onAccept={newEndValue => {
              newEndValue ? setFilterEndDate(newEndValue) : setFilterEndDate(filterEndDate);
              setReload(true);
            }}
            slotProps={{ textField: { size: "small", sx: { mr: 1, width: 150 } } }}
          />
        </LocalizationProvider>
        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <CustomerHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableSelectParent defaultValue={defaultCustomerHrchy} treeload={treeload} styles={{ width: 250 }} />
          <CustomerAutocomplete
            retrieve={retrieveFeatrueAnalysis}
            searchFields={ElasticSearchUtil.defaultCustomerSearchFields}
            searchValue={customerSearch}
            sx={{ mr: 1, width: 250 }}
          />
        </Box>
        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableSelectParent defaultValue={defaultValue} treeload={treeload} styles={{ width: 250 }}  />
          <DPItemAutocomplete
            retrieve={retrieveFeatrueAnalysis}
            searchFields={ElasticSearchUtil.defaultItemSearchFields}
            searchValue={itemSearch}
            sx={{ width: 250 }}
          />
        </Box>
        <ResetButton onClick={handleReset} />
      </FilterContainer>
      <SplitterContainer initialSizes={[7, 3]}>
        <Box
          className="chart-grid-area chart-area" sx={{ height: "100%", display: "flex" }}>
          <Box sx={{ width: "50%", height: "95%" }}>
            <Typography className="chart-title" variant="h6" sx={{ pl: 1, textAlign: "center", fontWeight: 500 }}>
              {t("relationBetweenActualSalesAndFeatures")}
            </Typography>
            <Box sx={{ height: "calc(100% - 15px)" }}>
              <ResponsiveContainer className="plot-container" >
                <ComposedChart data={salesData} margin={{ top: 10, right: 20, bottom: 30, left: 30 }}>
                  <CartesianGrid />
                  { scatterXConfig }
                  <YAxis type="number" tickFormatter={(tick) => numberFormatter(tick, yAxisAvg)} fontSize={FS.BODY}
                  label={{ value: t("salesQty"), offset: 0, position: "left", angle: -90, fontSize: FS.H2, fill: theme.palette.text.primary }} />
                  <Tooltip content={CustomTooltip} cursor={{ strokeDasharray: "3 3" }} />
                  <Scatter sx={{ height: "97%" }} name={t("salesQty")} dataKey="salesQty" fill={colorSet(0)} />
                  { lineConfig }
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Box>
          <Box sx={{ width: "50%", height: "97%", mr: 1 }}>
            <Typography className="chart-title" variant="h6" sx={{ pl: 1, textAlign: "center", fontWeight: 500 }}>
              {t("featureImportance")}
            </Typography>
            <Box sx={{ height: "calc(97% - 15px)", mr: 1 }}>
              <ChartComposed
                xAxis={["feature"]}
                data={chartData}
                dataSeries={chartSeries}
                options={{ yAxisDomain, decimalPoint:true }}
                tooltipAnimation={false}
              // options={{ minHeight: "200px" }}
              />
            </Box>
          </Box>
        </Box>
        <Box className="ag-theme-balham" sx={{ display: "flex", width: "100%", height: "calc(100% - 20px)" }}>
          <Box sx={{ width: "100%", height: "100%", pt: 2 }}>
            <AgGridReact
              ref={gridRef}
              rowData={gridData}
              {...defaultGridMemo}
              onGridReady={onGridReady}
              groupDefaultExpanded={-1}
              groupHideOpenParents
              groupDisplayType={"custom"}
              onCellClicked={onCellClicked}
              allowContextMenuWithControlKey
              suppressLastEmptyLineOnPaste
              overlayLoadingTemplate={`<span class="ag-overlay-loading-center">${t("MSG_Loading")}</span>`}
              suppressDragLeaveHidesColumns
              suppressMultiSort
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={open}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default withTranslation()(FeatureAnalysis);
